import { useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { AssessmentDatePopUp } from '../components/request';
import { calculateNextValidDate, assessmentTimeConfig } from '../utils/assessmentDateTimeCalculator';

type PickerProps = {
	open?: boolean;
	onAccept: (date: Date | null, index: number) => void;
	closePicker: () => void;
	onConfirm: () => void;
	dates: (Date | null)[];
	reqId: number;
	isUpdateAssessmentDatesLoading: boolean;
};

export const useBulkRequestAssessmentDatePicker = () => {
	const [assessmentDates, setAssessmentDates] = useState<PickerProps['dates']>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [reqId, setReqId] = useState(0);

	const onAccept = useCallback((date: Date, index: number) => {
		setAssessmentDates((prev) => {
			prev[index] = date;
			return prev;
		});
	}, []);

	const openPicker = useCallback(() => {
		setIsOpen(true);
	}, []);

	const closePicker = useCallback(() => {
		setIsOpen(false);
	}, []);

	return {
		AssessmentDatePicker,
		openPicker,
		setAssessmentDates,
		setReqId,
		assessmentDates,
		pickerProps: {
			onAccept,
			open: isOpen,
			dates: assessmentDates,
			closePicker,
			reqId,
		},
	};
};

const AssessmentDatePicker = ({ dates, ...rest }: PickerProps) => {
	const { open = false, onAccept, onConfirm, closePicker, reqId, isUpdateAssessmentDatesLoading } = rest;

	const theme = useTheme();

	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [editingDate, setEditingDate] = useState<Date>(new Date());

	const openDateSelection = (index: number) => {
		setEditingDate(dates[index] ?? calculateNextValidDate(new Date(), assessmentTimeConfig));
		setEditingIndex(index);
	};

	const closeDateSelection = () => {
		setEditingIndex(null);
	};

	const confirmDateSelection = () => {
		if (editingIndex !== null) {
			onAccept(editingDate, editingIndex);
		}
		setEditingIndex(null);
	};

	return (
		<Dialog open={open} fullWidth={true} maxWidth={'md'}>
			<DialogTitle>{`Choose your assessment dates for request ${reqId}`}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
					{[0, 1, 2].map((index) => (
						<Grid key={index} size={{ md: 4 }}>
							<DateTimePicker
								value={dates[index] ?? null}
								open={false}
								onOpen={() => openDateSelection(index)}
								ampm
								format={'dd MMM yyyy p'}
							/>
						</Grid>
					))}
					<Grid>
						<Typography variant="subtitle2" sx={{ color: theme.palette.grey[600] }}>
							By confirming the assessmesnt date/time, a confirmation email will be sent to the candidate. Please double
							check the date/time selected.
						</Typography>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button loading={isUpdateAssessmentDatesLoading} variant="outlined" color="primary" onClick={onConfirm}>
					<span>Confirm</span>
				</Button>
				<Button onClick={closePicker} autoFocus color="error" variant="outlined">
					Cancel
				</Button>
			</DialogActions>

			{/* per-date assessment date/time selection */}
			<Dialog open={editingIndex !== null} onClose={closeDateSelection}>
				<DialogContent>
					<AssessmentDatePopUp
						initialDate={editingDate}
						onChange={setEditingDate}
						reminder="By confirming the assessment date/time, a confirmation email will be sent to the candidate. Please double check the date/time selected."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={confirmDateSelection} variant="contained" color="primary">
						Confirm
					</Button>
					<Button onClick={closeDateSelection} color="error" variant="outlined">
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
};
